import { AnimationEvent, FC, useState } from 'react';
import classNames from 'classnames';
import { Toast as RadixToast } from 'radix-ui';

import { appearanceToStyle } from '../../model/ToastAppearance';
import { toast } from '../../model/toast';
import { ToastData } from '../../model/ToastData';
import { ToastVariant } from '../../model/ToastVariant';

import styles from './ToastItem.module.css';

export type ToastItemProps = {
  item: ToastData;
  className?: string;
};

const ToastItem: FC<ToastItemProps> = ({ item, className }) => {
  const [open, setOpen] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  const isPersistent = !Number.isFinite(item.duration) || item.duration <= 0;

  const handleClick = () => {
    if (item.closeOnClick) setOpen(false);
  };

  const handleAnimationEnd = (event: AnimationEvent<HTMLLIElement>) => {
    if (!open && event.target === event.currentTarget) toast.dismiss(item.id);
  };

  return (
    <RadixToast.Root
      type={item.variant === ToastVariant.ERROR || item.variant === ToastVariant.WARNING ? 'foreground' : 'background'}
      {...item.rootProps}
      className={classNames(styles.root, styles[item.variant], item.filled && styles.filled, className, item.className)}
      style={{ ...item.rootProps?.style, ...appearanceToStyle(item.appearance) }}
      duration={isPersistent ? Infinity : item.duration}
      open={open}
      onOpenChange={setOpen}
      onClick={handleClick}
      onPause={() => setIsPaused(true)}
      onResume={() => setIsPaused(false)}
      onAnimationEnd={handleAnimationEnd}>
      <RadixToast.Description className={styles.description}>{item.children}</RadixToast.Description>

      {!isPersistent && (
        <div
          className={styles.progress}
          aria-hidden="true"
          data-testid="toast-progress"
          style={{
            animationDuration: `${item.duration}ms`,
            animationPlayState: isPaused ? 'paused' : 'running',
          }}
        />
      )}
    </RadixToast.Root>
  );
};

export default ToastItem;
